import * as React from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { withRouter, RouteComponentProps } from 'react-router';
import { compose } from 'redux';
import { Drawer, Checkbox } from 'components/';
import * as Queries from 'api/graphql/queries';
import { OrderDirection, coupons } from '@api/graphql/types';
import GraphQLClient from '@api/graphql/GraphQLClient';
import ControlledTable from '@components/ControlledTable';
import { RowInfo } from 'react-table';
import { Column } from 'react-virtualized';
import { Paths } from '..';
import { Notification } from 'components';
import { DispatchProp, connect } from 'react-redux';
import { ListOptionType } from '@reducers/listReducer';
import { updateListOptions } from '@actions/listActions';
import { ApplicationState } from 'reducers/';
import { BooleanCell, OpenInNewCell } from '@components/Table/Cell';
import Colors from '@root/utils/Colors';
import moment from 'moment';
import CouponUtils from "@root/utils/CouponUtils";

interface StateType {
    data: any[];
    paginatorInfo: any;
    shouldFilterActive: boolean;
}

const CouponListDefaultOptions = {
    sortBy: {
        field: 'created_at',
        direction: OrderDirection.DESC,
    },
    count: 10,
    page: 0,
    filters: {
    },
};

const listName = 'couponList';

interface ReduxProps {
    listOptions: ListOptionType;
}

type Props = ReduxProps & DispatchProp & InjectedIntlProps & RouteComponentProps;

class CouponList extends React.Component<Props, StateType> {

    private TypeCell = (row : any) => {
        return this.label(`CouponType.${row.value}`);
    }

    private AppliedToCell = (row : any) => {
        return this.label(`CouponAppliedTo.${row.value}`);
    }

    public state = {
        data: [],
        paginatorInfo: {},
        shouldFilterActive: true,
    };

    private OpenCell = OpenInNewCell((rowInfo: any) => {
        return `${Paths.coupon}/${rowInfo.original.id}`;
    });

    private UsedCodesCountCell = (row : any): React.ReactNode => {
        if (row.original && row.original.codes && row.original.codes.data && row.original.codes.data.length > 0 && row.original.usage_count ) {
            return <>{CouponUtils.getUsedCodesCount(row.original.codes.data, row.original.usage_count)}</>;
        }
        return <>0</>;
    }

    private tableColumns = [
        {Header: '', sortable: false, Cell: this.OpenCell, width: 30},
        this.getDefaultColumnByName('created_at', { sortable: true }),
        this.getDefaultColumnByName('name', { sortable: true }),
        this.getDefaultColumnByName('type', { sortable: false, Cell: this.TypeCell }),
        this.getDefaultColumnByName('applied_to', { sortable: false, Cell: this.AppliedToCell }),
        this.getDefaultColumnByName('is_active', { sortable: false, Cell: BooleanCell }),
        this.getDefaultColumnByName('usage_count', { sortable: false }),
        this.getDefaultColumnByName('generated_codes_count', { sortable: true }),
        this.getDefaultColumnByName('used_codes_count', { sortable: false, Cell: this.UsedCodesCountCell }),
        this.getDefaultColumnByName('active_from', { sortable: false }),
        this.getDefaultColumnByName('active_to', { sortable: false }),
    ];

    public componentDidMount() {
        this.fetchData();
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.listOptions !== this.props.listOptions) {
            this.fetchData();
        }
    }

    private getDefaultColumnByName(name: string, options: any = {}) {
        return {
            Header: this.label(`${name}.title`),
            accessor: name,
            sortable: false,
            ...options,
        };
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.coupon.${id}` });
    }

    private getFilters() {
        const result = { ...this.props.listOptions.filters };
        Object.keys(result).forEach((key) => !result[key] && delete result[key]);
        result.is_active = this.state.shouldFilterActive || undefined;
        return result;
    }

    private getSortBy() {
        return {
            field: this.props.listOptions.sortBy.field.toUpperCase(),
            direction: this.props.listOptions.sortBy.direction.toUpperCase(),
        };
    }

    private async fetchData() {
        try {
            const variables = { ...this.props.listOptions, page: this.props.listOptions.page + 1, sortBy: this.getSortBy(), filters: this.getFilters() };
            const response = await GraphQLClient.query<coupons>({
                query: Queries.coupons,
                variables,
            });

            const data = response.data.coupons ? response.data.coupons.data : [];
            const paginatorInfo = response.data.coupons ? response.data.coupons.paginatorInfo : {};
            this.setState({ data, paginatorInfo });
        } catch {
            Notification.error(this.props.intl.formatMessage({ id: 'notification.failed' }));
        }
    }

    private onOptionsChange = (options: ListOptionType) => {
        this.props.dispatch(updateListOptions(listName, options));
    }

    private onRowClick = (finalState: any, rowInfo?: RowInfo, column?: Column, instance?: any): object | undefined => {
        let isActive;
        if (rowInfo) {
            isActive = rowInfo.row.is_active;
            if (isActive) {
                if (rowInfo.row.active_from && moment(rowInfo.row.active_from).isAfter(moment())) {
                    isActive = false;
                } else if (rowInfo.row.active_to && moment(rowInfo.row.active_to).isBefore(moment())) {
                    isActive = false;
                }
            }
            return {
                onClick: () => {
                    this.props.history.push(`${Paths.coupon}/${rowInfo.original.id}`);
                },
                style: {
                    cursor: 'pointer',
                    color: isActive ? undefined : Colors.inactive,
                },
            };
        }
        return {};
    }


    public render() {
        const addButton = <button className="btn btn-primary" onClick={(e: React.SyntheticEvent) => this.props.history.push(Paths.couponCreate)}>{this.label('couponCreate')}</button>;
        return (
            <Drawer headerButton={addButton}>
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-content">
                                <Checkbox
                                    checked={this.state.shouldFilterActive}
                                    label={this.props.intl.formatMessage({id: 'common.filterActive'})}
                                    onChange={(e: React.ChangeEvent<any>) => {
                                        this.setState({ shouldFilterActive: e.currentTarget.checked }, () => this.fetchData());
                                    }}
                                />
                                <ControlledTable
                                    listName={listName}
                                    defaultOptions={CouponListDefaultOptions}
                                    columns={this.tableColumns}
                                    options={this.props.listOptions}
                                    data={this.state.data}
                                    paginatorInfo={this.state.paginatorInfo}
                                    onOptionsChange={this.onOptionsChange}
                                    onRowClick={this.onRowClick}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Drawer>
        );
    }
}

const mapStateToProps = ({ lists }: ApplicationState): ReduxProps => {
    return { listOptions: (lists ? (lists[listName] || CouponListDefaultOptions) : CouponListDefaultOptions) };
};


export default compose(
    withRouter,
    injectIntl,
    connect(mapStateToProps),
)(CouponList);



// WEBPACK FOOTER //
// ./src/modules/Coupon/pages/CouponList.tsx